/**
 * DBCredentialsCore.tsx
 * 
 * Pure UI component for displaying database credentials.
 * No auth, no data fetching, no modal wrapper.
 * 
 * Used by:
 * - DBCredentialsModal.tsx (main app - wrapped in modal, uses JWT auth)
 * - widget-bundle.tsx (widget embed - uses widget API auth)
 * 
 * SINGLE SOURCE OF TRUTH for credentials UI.
 */

import React, { useState } from 'react';
import {
  Database,
  Eye,
  EyeOff,
  Copy,
  Check,
  Info,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { DBCredentialsCoreProps, WidgetTheme } from '../widget/types';
import { useWidgetTheme, mainAppTheme } from '../widget/useWidgetTheme';

// ==================== CREDENTIAL FIELD ====================

interface CredentialFieldProps {
  label: string;
  value: string;
  isPassword?: boolean;
  showPassword?: boolean;
  onTogglePassword?: () => void;
  onCopy: () => void;
  copied: boolean;
  fullWidth?: boolean;
  theme: WidgetTheme;
}

const CredentialField: React.FC<CredentialFieldProps> = ({
  label,
  value,
  isPassword,
  showPassword,
  onTogglePassword,
  onCopy,
  copied,
  fullWidth,
  theme,
}) => {
  const [copyHovered, setCopyHovered] = useState(false);
  const [eyeHovered, setEyeHovered] = useState(false);

  return (
    <div
      style={{
        padding: '14px 16px',
        backgroundColor: theme.colors.bg,
        border: `1px solid ${theme.colors.border}`,
        borderRadius: theme.radius.md,
        transition: 'all 0.15s ease',
        gridColumn: fullWidth ? '1 / -1' : 'auto',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p
            style={{
              fontSize: '0.6875rem',
              fontWeight: 600,
              color: theme.colors.textTertiary,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              margin: '0 0 6px 0',
            }}
          >
            {label}
          </p>
          <p
            style={{
              fontSize: '0.875rem',
              fontWeight: 600,
              color: theme.colors.text,
              fontFamily: theme.fontMono,
              wordBreak: 'break-all',
              margin: 0,
            }}
          >
            {isPassword && !showPassword ? '••••••••••••••••' : value}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '4px', marginLeft: '12px', flexShrink: 0 }}>
          {isPassword && (
            <button
              onClick={onTogglePassword}
              onMouseEnter={() => setEyeHovered(true)}
              onMouseLeave={() => setEyeHovered(false)}
              title={showPassword ? 'Hide' : 'Show'}
              style={{
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: eyeHovered ? theme.colors.primaryLight : theme.colors.bgAlt,
                border: 'none',
                borderRadius: theme.radius.sm,
                cursor: 'pointer',
                color: eyeHovered ? theme.colors.primary : theme.colors.textTertiary,
                transition: 'all 0.15s',
              }}
            >
              {showPassword ? <EyeOff size={16} strokeWidth={1.5} /> : <Eye size={16} strokeWidth={1.5} />}
            </button>
          )}
          <button
            onClick={onCopy}
            onMouseEnter={() => setCopyHovered(true)}
            onMouseLeave={() => setCopyHovered(false)}
            title={copied ? 'Copied!' : 'Copy'}
            style={{
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: copied ? theme.colors.successBg : copyHovered ? theme.colors.primaryLight : theme.colors.bgAlt,
              border: 'none',
              borderRadius: theme.radius.sm,
              cursor: 'pointer',
              color: copied ? theme.colors.success : copyHovered ? theme.colors.primary : theme.colors.textTertiary,
              transition: 'all 0.15s',
            }}
          >
            {copied ? <Check size={16} strokeWidth={2} /> : <Copy size={16} strokeWidth={1.5} />}
          </button>
        </div>
      </div>
    </div>
  );
};

// ==================== MAIN COMPONENT ====================

const DBCredentialsCore: React.FC<DBCredentialsCoreProps> = ({
  credentials,
  loading,
  error,
  mode,
  theme: themeOverrides,
  onRetry,
  onCopySuccess,
  onCopyError,
}) => {
  // Use main app theme for modal mode, widget theme for embedded
const widgetTheme = useWidgetTheme();
const baseTheme = mode === 'modal' ? mainAppTheme : widgetTheme;  
  const theme = themeOverrides ? { ...baseTheme, ...themeOverrides } : baseTheme;
  
const [showPassword, setShowPassword] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      onCopySuccess?.('Copied to clipboard');
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      onCopyError?.('Failed to copy to clipboard');
    }
  };

  // Loading state
  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '48px',
          gap: '12px',
        }}
      >
        <Loader2
          size={28}
          strokeWidth={2}
          color={theme.colors.primary}
          style={{ animation: 'spin 1s linear infinite' }}
        />
        <span style={{ color: theme.colors.textTertiary, fontSize: '0.875rem' }}>
          Loading credentials...
        </span>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          backgroundColor: theme.colors.errorBg,
          border: '1px solid #fca5a5',
          borderRadius: theme.radius.md,
          padding: '16px',
        }}
      >
        <AlertCircle
          size={18}
          strokeWidth={1.5}
          color={theme.colors.error}
          style={{ flexShrink: 0, marginTop: '1px' }}
        />
        <div>
          <p style={{ fontSize: '0.875rem', fontWeight: 500, color: theme.colors.error, margin: 0 }}>
            {error}
          </p>
          {onRetry && (
            <button
              onClick={onRetry}
              style={{
                marginTop: '8px',
                padding: '6px 12px',
                fontSize: '0.8125rem',
                backgroundColor: theme.colors.bg,
                border: '1px solid #fca5a5',
                borderRadius: theme.radius.sm,
                cursor: 'pointer',
                color: theme.colors.error,
              }}
            >
              Try Again
            </button>
          )}
        </div>
      </div>
    );
  }

  // No data
  if (!credentials) {
    return null;
  }

// Main content
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', fontFamily: theme.fontSans }}>
      {/* Header - only show in embedded mode (modal has its own header) */}
      {mode === 'embedded' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div
            style={{
              width: '44px',
              height: '44px',
              borderRadius: theme.radius.md,
              background: 'linear-gradient(135deg, #4f46e5 0%, #818cf8 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 6px -1px rgba(79, 70, 229, 0.3)',
            }}
          >
            <Database size={22} strokeWidth={1.5} color="#ffffff" />
          </div>
          <div>
            <h2 style={{ fontSize: '1.125rem', fontWeight: 700, color: theme.colors.text, margin: 0 }}>
              Database Credentials
            </h2>
            <p style={{ fontSize: '0.875rem', color: theme.colors.textTertiary, margin: '2px 0 0 0' }}>
	      Credentials for Integration Apps              
            </p>
          </div>
        </div>
      )}

      {/* Info Banner */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          backgroundColor: theme.colors.infoBg,
          border: '1px solid #93c5fd',
          borderRadius: theme.radius.md,
          padding: '14px 16px',
        }}
      >
        <Info
          size={18}
          strokeWidth={1.5}
          color={theme.colors.primary}
          style={{ flexShrink: 0, marginTop: '1px' }}
        />
        <p style={{ fontSize: '0.875rem', color: theme.colors.textMuted, lineHeight: 1.6, margin: 0 }}>
	These <strong>read-only</strong> credentials are used when registering an Integration App
          under <strong>Settings → Integration</strong>. They provide access to tables created by your
          API endpoints and imported Excel/CSV files. There is no external access to your
          workspace database — these credentials only work within the platform.
        </p>
      </div>

      {/* Connection Details */}
      <div
        style={{
          backgroundColor: theme.colors.bgAlt,
          border: `1px solid ${theme.colors.border}`,
          borderRadius: theme.radius.md,
          padding: '20px',
        }}
      >
        <p
          style={{
            fontSize: '0.6875rem',
            fontWeight: 600,
            color: theme.colors.textTertiary,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            margin: '0 0 16px 0',
            paddingBottom: '12px',
            borderBottom: `1px solid ${theme.colors.border}`,
          }}
        >
          Connection Details
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <CredentialField
            label="Host"
            value={credentials.db_host}
            onCopy={() => copyToClipboard(credentials.db_host, 'host')}
            copied={copiedField === 'host'}
            theme={theme}
          />
          <CredentialField
            label="Port"
            value={credentials.db_port.toString()}
            onCopy={() => copyToClipboard(credentials.db_port.toString(), 'port')}
            copied={copiedField === 'port'}
            theme={theme}
          />
          <CredentialField
            label="Database"
            value={credentials.db_name}
            onCopy={() => copyToClipboard(credentials.db_name, 'database')}
            copied={copiedField === 'database'}
            fullWidth
            theme={theme}
          />
          <CredentialField
            label="Username"
            value={credentials.db_readonly_user}
            onCopy={() => copyToClipboard(credentials.db_readonly_user, 'username')}
            copied={copiedField === 'username'}
            fullWidth
            theme={theme}
          />
          <CredentialField
            label="Password"
            value={credentials.db_readonly_password}
            isPassword
            showPassword={showPassword}
            onTogglePassword={() => setShowPassword(!showPassword)}
            onCopy={() => copyToClipboard(credentials.db_readonly_password, 'password')}
            copied={copiedField === 'password'}
            fullWidth
            theme={theme}
          />
        </div>
      </div>

</div>
  );
};


export default DBCredentialsCore;
